<template>
	<div
		ref="noticeEl"
		class="fixed top-0 z-[1000] w-full bg-[#1A2332] text-white"
	>
		<div
			class="relative mx-auto flex max-w-6xl items-center gap-4 px-4"
			:class="isSecurityNoticeCollapsed ? 'py-2' : 'py-4'"
		>
			<IconWarning class="h-6 w-6 fill-white" />

			<div class="min-w-0 flex-1">
				<p
					v-if="isSecurityNoticeCollapsed"
					class="truncate text-base text-white"
				>
					We're upgrading our security – some features are temporarily
					unavailable.
					<button
						type="button"
						class="ml-1 font-medium underline underline-offset-2 hover:text-gray-200"
						@click="toggleSecurityNoticeCollapsed"
					>
						Show details
					</button>
				</p>
				<div
					v-else
					class="flex flex-col gap-2 text-left xl:pl-4 xl:pr-20"
				>
					<p class="text-white">
						Due to recent global cybersecurity vulnerabilities, we
						have temporarily disabled certain functions on our
						website as a precautionary measure to protect the
						security and integrity of your data. Our team is
						actively working to restore full functionality as
						quickly as possible and we appreciate your patience
						during this time.
					</p>
					<p class="text-white">
						If you require any assistance in the meantime, please do
						not hesitate to contact us at
						<CarbarLink
							href="mailto:support@carbar.com.au"
							target="_blank"
							text="support@carbar.com.au"
							font-size="text-sm md:text-base"
							class="inline-block text-white hover:text-gray-300"
							underline-color="decoration-white hover:decoration-gray-300"
						/>
						or call us on
						<CarbarLink
							href="tel:1300620685"
							target="_blank"
							text="1300 620 685"
							font-size="text-sm md:text-base"
							class="inline-block text-white hover:text-gray-300"
							underline-color="decoration-white hover:decoration-gray-300"
						/>
						<span>.</span>
					</p>
				</div>
			</div>

			<button
				type="button"
				class="flex shrink-0 items-center justify-center rounded p-1.5 text-white transition-colors hover:bg-white/10"
				:aria-expanded="!isSecurityNoticeCollapsed"
				:aria-label="
					isSecurityNoticeCollapsed
						? 'Expand security notice'
						: 'Minimize security notice'
				"
				@click="toggleSecurityNoticeCollapsed"
			>
				<div
					class="caret"
					:class="{ open: !isSecurityNoticeCollapsed }"
				>
					<div class="on-open-rotate-180 duration-200">
						<IconArrowDown class="text-white" />
					</div>
				</div>
			</button>
		</div>
	</div>
</template>

<script setup>
import IconArrowDown from '@/assets/svg/common/ico-arrow_down.svg'
import IconWarning from '@/assets/svg/common/ico-warning.svg'
import CarbarLink from '@/components/links/CarbarLink.vue'

const noticeEl = ref(null)
const {
	isSecurityNoticeCollapsed,
	setSecurityNoticeHeight,
	toggleSecurityNoticeCollapsed,
} = useSecurityNotice()

let resizeObserver

const measureHeight = () => {
	const height = noticeEl.value?.offsetHeight ?? 0
	setSecurityNoticeHeight(height)
}

// Wait for Vue DOM + browser layout so collapse/expand measures the new size.
const updateHeight = () => {
	nextTick(() => {
		requestAnimationFrame(() => {
			requestAnimationFrame(measureHeight)
		})
	})
}

watch(isSecurityNoticeCollapsed, updateHeight)

onMounted(() => {
	updateHeight()
	if (noticeEl.value && typeof ResizeObserver !== 'undefined') {
		resizeObserver = new ResizeObserver(() => {
			measureHeight()
		})
		resizeObserver.observe(noticeEl.value)
	}
})

onBeforeUnmount(() => {
	resizeObserver?.disconnect()
})
</script>
